'use client';

import { FC, ReactNode, useState, useMemo, useEffect } from 'react';
import classNames from 'classnames';
import Pagination from '../Pagination/Pagination';
import styles from './dataTable.module.sass';

export type ColumnAlignment = 'left' | 'center' | 'right';

export interface DataTableColumn {
  /** Unique identifier for the column */
  key: string;
  /** Display label for the column header */
  label: string;
  /** Whether this column is sortable */
  sortable?: boolean;
  /** Text alignment for the column */
  align?: ColumnAlignment;
}

export interface DataTableProps {
  /** Column definitions */
  columns: DataTableColumn[];
  /** Data rows to display (array of objects) */
  data: Record<string, ReactNode>[];
  /** Callback when a sortable column header is clicked */
  onSort?: (columnKey: string) => void;
  /** Current sort column key (if sorted) */
  sortBy?: string;
  /** Current sort direction */
  sortOrder?: 'asc' | 'desc';
  /** Optional footer rows */
  footer?: Record<string, ReactNode>[];
  /** Custom row class names or styling logic */
  rowClassName?: (
    row: Record<string, ReactNode>,
    index: number
  ) => string | undefined;
  /** Message to show when no data is provided */
  emptyMessage?: ReactNode;
  /** Callback when a row is clicked */
  onRowClick?: (row: Record<string, ReactNode>, index: number) => void;
  /** Rows per page — omit or 0 to disable pagination */
  pageSize?: number;
  /** Additional CSS class */
  className?: string;
}

const DataTable: FC<DataTableProps> = ({
  columns,
  data,
  onSort,
  sortBy,
  sortOrder,
  footer,
  rowClassName,
  onRowClick,
  emptyMessage = 'No data available',
  pageSize = 0,
  className,
}) => {
  const [currentPage, setCurrentPage] = useState(1);

  const totalPages = pageSize > 0 ? Math.ceil(data.length / pageSize) : 1;
  const paginated = pageSize > 0;

  // Reset page when data changes
  const dataLen = data.length;
  useEffect(() => { setCurrentPage(1); }, [dataLen]);

  const visibleData = useMemo(() => {
    if (!paginated) return data;
    const start = (currentPage - 1) * pageSize;
    return data.slice(start, start + pageSize);
  }, [data, currentPage, pageSize, paginated]);

  const handleHeaderClick = (column: DataTableColumn) => {
    if (column.sortable && onSort) {
      onSort(column.key);
    }
  };

  const getAlignmentClass = (align?: ColumnAlignment) => {
    switch (align) {
      case 'center':
        return styles.centerAlign;
      case 'right':
        return styles.rightAlign;
      case 'left':
      default:
        return styles.leftAlign;
    }
  };

  const isSortableColumn = (column: DataTableColumn) =>
    column.sortable && onSort;

  const startRow = paginated ? (currentPage - 1) * pageSize + 1 : 1;
  const endRow = paginated ? Math.min(currentPage * pageSize, data.length) : data.length;

  return (
    <div className={classNames(styles.container, className)}>
      {data.length === 0 ? (
        <div className={styles.empty}>{emptyMessage}</div>
      ) : (
        <>
          <table className={styles.table}>
            <thead>
              <tr>
                {columns.map(column => (
                  <th
                    key={column.key}
                    className={classNames(getAlignmentClass(column.align), {
                      [styles.sortable]: isSortableColumn(column),
                      [styles.sorted]: sortBy === column.key,
                    })}
                    onClick={() => handleHeaderClick(column)}
                    role={isSortableColumn(column) ? 'button' : undefined}
                    tabIndex={isSortableColumn(column) ? 0 : undefined}
                    onKeyDown={e => {
                      if (
                        isSortableColumn(column) &&
                        (e.key === 'Enter' || e.key === ' ')
                      ) {
                        e.preventDefault();
                        handleHeaderClick(column);
                      }
                    }}
                  >
                    {column.label}
                    {sortBy === column.key && (
                      <span
                        className={classNames(styles.sortIndicator, {
                          [styles.desc]: sortOrder === 'desc',
                        })}
                      >
                        {sortOrder === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visibleData.map((row, rowIndex) => (
                <tr
                  key={rowIndex}
                  className={classNames(rowClassName?.(row, rowIndex), {
                    [styles.clickableRow]: !!onRowClick,
                  })}
                  onClick={() => onRowClick?.(row, rowIndex)}
                >
                  {columns.map(column => (
                    <td
                      key={`${rowIndex}-${column.key}`}
                      className={getAlignmentClass(column.align)}
                    >
                      {row[column.key]}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
            {footer && footer.length > 0 && (
              <tfoot>
                {footer.map((footerRow, footerIndex) => (
                  <tr key={`footer-${footerIndex}`}>
                    {columns.map(column => (
                      <td
                        key={`footer-${footerIndex}-${column.key}`}
                        className={getAlignmentClass(column.align)}
                      >
                        {footerRow[column.key]}
                      </td>
                    ))}
                  </tr>
                ))}
              </tfoot>
            )}
          </table>
          {paginated && totalPages > 1 && (
            <div className={styles.paginationBar}>
              <span className={styles.paginationInfo}>
                {startRow}–{endRow} of {data.length}
              </span>
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default DataTable;
